import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TIME_HOURS } from 'emma-common-ts';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';

export interface TimeDurationOption {
  value: 'day' | 'hour' | 'month' | 'none';
  label: string;
  min: number;
  max: number;
  factor: number;
}
// type ItemsToStringCallback = (item: any) => string;
// type LabelCallback = (item: any) => string;

export const EMMA_TIME_RANGE_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATimeDurationComponent),
  multi: true,
};

const EMMA_TIME_RANGE_VALIDATOR = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMATimeDurationComponent),
  multi: true,
};

@Component({
    selector: 'emma-timeduration',
    templateUrl: './emma-timeduration.component.html',
    providers: [
        EMMA_TIME_RANGE_CONTROL_VALUE_ACCESSOR,
        EMMA_TIME_RANGE_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMATimeDurationComponent,
        },
    ],
    standalone: false
})
export class EMMATimeDurationComponent
  extends EMMAFormElementComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator
{
  @Input() units: TimeDurationOption[] = [
    { value: 'none', label: $localize`Vitalicia`, min: 0, max: 0, factor: 0 },
    { value: 'hour', label: $localize`Horas`, min: 1, max: 23, factor: 1 },
    {
      value: 'day',
      label: $localize`Días`,
      min: 1,
      max: 29,
      factor: TIME_HOURS.DAY,
    },
    {
      value: 'month',
      label: $localize`Meses`,
      min: 1,
      max: 12,
      factor: TIME_HOURS.MONTH,
    },
  ];
  unitsMapping: { [key: number]: { [k: string]: string } } = {};

  @Input() color?: string;

  sliderValue: number;
  _selectedUnit = 'none';
  unitRef: number = -1;

  sliderOptions = {
    floor: 1,
    ceil: 23,
    hidePointerLabels: 'true',
    showSelectionBar: 'true',
    disabled: this.disabled,
  };

  private innerValue = 0;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: unknown) => void = noop;

  @Output() override $change = new EventEmitter<number>();
  @Output() override $blur = new EventEmitter<number>();

  constructor() {
    super();
    this.sliderValue = this.value || 0;
    this.updateUnitsMapping();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      this.sliderOptions = { ...this.sliderOptions, disabled: this.disabled };
    }
    if ('units' in changes) {
      this.unitRef = -1;
      this.updateUnitsMapping();
    }
  }

  updateUnitsMapping() {
    this.unitsMapping = this.units.reduce((acc: { [key: number]: { [k: string]: string } }, unit, index) => {
      if (unit.value === 'hour') {
        acc[index] = {
          '=1': $localize`# hora`,
          other: $localize`# horas`,
        };
      } else if (unit.value === 'day') {
        acc[index] = {
          '=1': $localize`# día`,
          other: $localize`# días`,
        };
      } else if (unit.value === 'month') {
        acc[index] = {
          '=1': $localize`# mes`,
          other: $localize`# meses`,
        };
      } else if (unit.value === 'none') {
        acc[index] = {
          other: $localize`vitalicia`,
        };
      }
      return acc;
    }, {});
  }

  onUnitChange = (selectedUnit: string) => {
    this.unitRef = this.units.findIndex((u) => u.value === selectedUnit) || 0;
    this._selectedUnit = this.units[this.unitRef].value;
    this.sliderOptions = {
      ...this.sliderOptions,
      floor: this.units[this.unitRef].min,
      ceil: this.units[this.unitRef].max,
    };
    const unit = this.units[this.unitRef];
    this.value = unit.min * unit.factor;
  };

  onValueChange = (value: number) => {
    this.sliderValue = value || 0;
    this.updateValue();
  };

  updateValue(sliderValue: number = this.sliderValue, unitIndex: number = this.unitRef) {
    const unit = this.units[unitIndex];
    const value = unit
      ? Math.min(Math.max(unit.min * unit.factor, sliderValue * unit.factor), unit.max * unit.factor)
      : 0;
    if (this.value !== value) {
      this.value = value;
    }
  }

  getUnitByValue(): number {
    if (!this.innerValue) {
      const noneIndex = this.units.findIndex((unit) => unit.value === 'none');
      return noneIndex !== -1 ? noneIndex : 0;
    }
    for (let unitRef = this.units.length - 1; unitRef >= 0; --unitRef) {
      if (this.innerValue >= this.units[unitRef].factor) {
        return unitRef;
      }
    }
    return 0;
  }

  configInitialSlider() {
    const prevUnitRef = this.unitRef;
    this.unitRef = this.getUnitByValue();
    const unit = this.units[this.unitRef];
    if (this.unitRef !== prevUnitRef) {
      this._selectedUnit = unit.value;
      this.sliderOptions = {
        ...this.sliderOptions,
        floor: unit.min,
        ceil: unit.max,
      };
    }
    this.sliderValue = unit?.factor
      ? Math.min(Math.max(unit.min, this.innerValue / unit.factor), unit.max)
      : 0;
  }

  // get accessor
  get value(): number {
    return this.innerValue;
  }

  // set accessor including call the onchange callback
  set value(v: number) {
    v = v || 0;
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(this.innerValue);
      this.$change.emit(this.innerValue);
    }
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit(this.value);
  }

  // From ControlValueAccessor interface
  writeValue(value: number | null) {
    if (value !== this.value) {
      this.value = value || 0;
      this.configInitialSlider();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: (_: unknown) => void) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }
}
